/**
 * State behind the in-app workspace invitation modal and the Pending rows of
 * the avatar menu (Figma MVP-DESIGNES 10216:3390, 10219:22067, 10219:22909).
 *
 * The invitee is not a member of the inviting workspace yet, so the
 * workspace-scoped updates socket cannot tell them about it: the list is
 * polled instead - once when the shell mounts, every `INVITATIONS_POLL_MS`,
 * and whenever the tab becomes visible again.
 *
 * A *modal session* is the ordered list of invitation ids the modal walks
 * through (`queue`) and where it is (`position`); the counter reads
 * `position + 1 / queue.length`.  A session opens on its own for every
 * invitation that was never dismissed and never shown this page load, or for
 * one invitation when its Pending row is clicked (`openFor`).  Accept and
 * Decline move to the next invitation; closing the modal any other way
 * (Escape, overlay) dismisses the rest of the session: they stay pending for
 * the admin but the modal never opens for them on its own again, on any
 * device, because `dismissed` is stored on the invitation.
 *
 * Accepting does not switch the active workspace.  The joined workspace is
 * appended to the switcher through `applyWorkspaceJoined`, and once the
 * session ends with at least one accept, `menuOpenRequest` ticks so the
 * avatar menu opens with the "Welcome aboard" callout on `acceptedIds`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sparkToast } from '@spark';
import { acceptInvitation, declineInvitation, dismissInvitations, fetchMyInvitations } from '../api/invitationsApi';

export const INVITATIONS_POLL_MS = 60_000;

/** Statuses after which the invitation is gone for good; the modal moves on instead of retrying. */
const SETTLED_STATUSES = new Set([404, 409, 410]);

const EMPTY = [];

export function usePendingInvitationsState({ enabled, applyWorkspaceJoined }) {
    const [invitations, setInvitations] = useState(EMPTY);
    const [queue, setQueue] = useState(EMPTY);
    const [position, setPosition] = useState(0);
    const [busy, setBusy] = useState(false);
    const [acceptedIds, setAcceptedIds] = useState(EMPTY);
    const [menuOpenRequest, setMenuOpenRequest] = useState(0);
    // Ids whose dismissal is in flight or done: a poll that races the POST
    // must not flip them back to `dismissed: false` and re-open the modal.
    const locallyDismissed = useRef(new Set());
    // Ids the modal already opened for on this page load, so a refetch while
    // the session is still running does not start a second session.
    const shown = useRef(new Set());
    const acceptedInSession = useRef(0);

    const load = useCallback(async () => {
        if (!enabled) return;
        const result = await fetchMyInvitations();
        if (!result.ok) return;
        const rows = Array.isArray(result.data?.invitations) ? result.data.invitations : [];
        setInvitations(
            rows.map((row) => (locallyDismissed.current.has(row.id) ? { ...row, dismissed: true } : row))
        );
    }, [enabled]);

    useEffect(() => {
        if (!enabled) {
            setInvitations(EMPTY);
            return undefined;
        }
        load();
        const timer = setInterval(load, INVITATIONS_POLL_MS);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') load();
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [enabled, load]);

    const sessionOpen = queue.length > 0;

    // Open a session for every never-dismissed invitation that has not been shown yet.
    useEffect(() => {
        if (sessionOpen) return;
        const fresh = invitations.filter((row) => !row.dismissed && !shown.current.has(row.id)).map((row) => row.id);
        if (fresh.length === 0) return;
        fresh.forEach((id) => shown.current.add(id));
        acceptedInSession.current = 0;
        setQueue(fresh);
        setPosition(0);
    }, [invitations, sessionOpen]);

    const current = useMemo(() => {
        if (!sessionOpen || position >= queue.length) return null;
        return invitations.find((row) => row.id === queue[position]) || null;
    }, [invitations, queue, position, sessionOpen]);

    const endSession = useCallback(() => {
        setQueue(EMPTY);
        setPosition(0);
        if (acceptedInSession.current > 0) setMenuOpenRequest((n) => n + 1);
        acceptedInSession.current = 0;
    }, []);

    const advance = useCallback(() => {
        const next = position + 1;
        if (next >= queue.length) endSession();
        else setPosition(next);
    }, [position, queue.length, endSession]);

    // The invitation under the cursor disappeared (the admin revoked it and a
    // poll dropped it): skip it rather than show an empty modal.
    useEffect(() => {
        if (sessionOpen && position < queue.length && !current) advance();
    }, [sessionOpen, position, queue.length, current, advance]);

    const removeInvitation = useCallback((id) => {
        setInvitations((prev) => prev.filter((row) => row.id !== id));
    }, []);

    const decide = useCallback(
        async (request, onSuccess) => {
            if (!current || busy) return;
            setBusy(true);
            const result = await request(current.id);
            setBusy(false);
            if (result.ok) {
                onSuccess?.(result.data);
                removeInvitation(current.id);
                advance();
                return;
            }
            sparkToast.error(result.error);
            if (!SETTLED_STATUSES.has(result.status)) return;
            removeInvitation(current.id);
            advance();
        },
        [current, busy, removeInvitation, advance]
    );

    const accept = useCallback(
        () =>
            decide(acceptInvitation, (data) => {
                const joined = {
                    id: data?.workspace_id || current.workspace_id,
                    name: data?.workspace_name || current.workspace_name,
                    role: data?.role || current.role,
                    logo: current.workspace_logo || null,
                };
                applyWorkspaceJoined?.(joined);
                acceptedInSession.current += 1;
                setAcceptedIds((prev) => (prev.includes(joined.id) ? prev : [...prev, joined.id]));
            }),
        [decide, current, applyWorkspaceJoined]
    );

    const decline = useCallback(() => decide(declineInvitation), [decide]);

    /** Escape / overlay: the rest of the session is dismissed and the modal closes. */
    const dismiss = useCallback(() => {
        const remaining = queue
            .slice(position)
            .filter((id) => invitations.some((row) => row.id === id && !row.dismissed));
        remaining.forEach((id) => locallyDismissed.current.add(id));
        if (remaining.length > 0) {
            setInvitations((prev) => prev.map((row) => (remaining.includes(row.id) ? { ...row, dismissed: true } : row)));
            dismissInvitations(remaining);
        }
        endSession();
    }, [queue, position, invitations, endSession]);

    /** A Pending row was clicked: a one-invitation session, counter hidden. */
    const openFor = useCallback(
        (id) => {
            if (!invitations.some((row) => row.id === id)) return;
            shown.current.add(id);
            acceptedInSession.current = 0;
            setQueue([id]);
            setPosition(0);
        },
        [invitations]
    );

    const clearAccepted = useCallback(() => setAcceptedIds(EMPTY), []);

    const dismissed = useMemo(() => invitations.filter((row) => row.dismissed), [invitations]);

    return {
        invitations,
        /** Invitations closed without a decision: the Pending rows of the avatar menu. */
        dismissed,
        current,
        /** 1-based index of `current` in the session and the session length (for `n/total invitations`). */
        position: sessionOpen ? position + 1 : 0,
        total: queue.length,
        busy,
        accept,
        decline,
        dismiss,
        openFor,
        refresh: load,
        acceptedIds,
        clearAccepted,
        menuOpenRequest,
    };
}
